import { UserDetails } from '../../interfaces/UserDetailsInterface';
import Loader from '../reusable/Loader';
import Panel from '../reusable/Panel';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableRow, TableCell, TableContainer, Paper, Tabs, Tab, TableHead } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import dayjs from 'dayjs';
import { getEOAConfig, getConfiguredSchools } from '../../services/eoaConfigServices';
import { ConfiguredEOASchool } from '../../interfaces/EOAInterface';
import useSchools from '../hooks/useSchools';
import useSchoolReportStatusChannel from '../hooks/useSchoolReportStatusChannel';
import { getWorkflowStatuses, SchoolWorkflowStatuses } from '../../services/workflowService';
import SurveyReportStatus from '../survey-reports/SurveyReportStatus';
import FocusGroupReportStatus from '../focus-groups/FocusGroupReportStatus';
import SchoolReportStatus from '../school-reports/SchoolReportStatus';
import TranscriptSelectionStatus from '../transcripts/TranscriptSelectionStatus';
import StudentVoiceStatus from '../student-voice/StudentVoiceStatus';
import SjwPhotoUploadStatus from '../sjw-photo-upload/SjwPhotoUploadStatus';
import SchoolProgressTag from './SchoolProgressTag';
import { sharedTabStyles } from '../reusable/sharedTabStyles';
import { NAVIGATION_LINKS_DATA, USER_ROLES } from '../../constants';
import './DashboardContent.css';

interface DashboardContentProps {
  userDetails: UserDetails;
}

interface Workshop {
  title: string;
  date: string;
  monthAbbr: string;
  day: string;
  formattedDateTime: string;
}

interface SchoolProgress {
  id: string;
  name: string;
  totalCategories: number;
  categoryStatuses: CategoryStatus;
}

interface CategoryDetail {
  name: string;
  statusComponent: JSX.Element;
  dueDate: string;
}

interface CategoryStatus {
  surveyReport: string | null;
  focusGroupReport: string | null;
  transcriptSelection: string | null;
  studentVoice: string | null;
  schoolReport: string | null;
  sjwPhotoUpload: string | null;
}

const getCategoryDetails = (school: SchoolProgress, sjwDateString: string | null, role?: string): CategoryDetail[] => {
  const dueDate = sjwDateString ? dayjs(sjwDateString).format('M/D/YYYY') : '8/1/2025';
  const statuses: CategoryStatus = school.categoryStatuses || {
    surveyReport: null,
    focusGroupReport: null,
    transcriptSelection: null,
    studentVoice: null,
    schoolReport: null,
    sjwPhotoUpload: null,
  };

  const allCategories = [
    {
      name: 'Survey report',
      statusComponent: <SurveyReportStatus status={statuses.surveyReport} size="small" />,
      dueDate,
    },
    {
      name: 'Focus group report',
      statusComponent: <FocusGroupReportStatus status={statuses.focusGroupReport} size="small" />,
      dueDate,
    },
    {
      name: 'Transcript selection',
      statusComponent: <TranscriptSelectionStatus status={statuses.transcriptSelection} size="small" />,
      dueDate,
    },
    {
      name: 'Student voice for reports',
      statusComponent: <StudentVoiceStatus status={statuses.studentVoice} size="small" />,
      dueDate,
    },
    {
      name: 'Student Journey Workshop photo upload',
      statusComponent: <SjwPhotoUploadStatus status={statuses.sjwPhotoUpload} size="small" />,
      dueDate,
    },
    {
      name: 'School report',
      statusComponent: <SchoolReportStatus status={statuses.schoolReport} size="small" userRole={role} />,
      dueDate,
    },
  ];

  if (role === USER_ROLES.FACILITATOR) {
    return allCategories.filter(
      (category) =>
        category.name === 'Transcript selection' ||
        category.name === 'Student voice for reports' ||
        category.name === 'Student Journey Workshop photo upload'
    );
  }

  return allCategories;
};

const ProgressStatusView = ({ userDetails }: DashboardContentProps) => {
  const navigate = useNavigate();
  const { updateTranscriptSelectionSchool } = useSchools();
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sjwDate, setSjwDate] = useState<string | null>(null);
  const [schoolsProgress, setSchoolsProgress] = useState<SchoolProgress[]>([]);
  const [expandedSchools, setExpandedSchools] = useState<Set<string>>(new Set());

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const toggleSchoolExpansion = (schoolId: string) => {
    setExpandedSchools((prev) => {
      const newSet = new Set(prev);
      newSet.has(schoolId) ? newSet.delete(schoolId) : newSet.add(schoolId);
      return newSet;
    });
  };

  const handleCategoryNavigation = (categoryName: string, schoolId: string) => {
    if (categoryName === 'Survey report') {
      navigate('/survey-reports');
    } else if (categoryName === 'Focus group report') {
      navigate(`/school-focus-group/${schoolId}`);
    } else if (categoryName === 'Transcript selection') {
      updateTranscriptSelectionSchool(schoolId);
      navigate('/transcripts');
    } else if (categoryName === 'Student voice for reports') {
      navigate(`/student-voice-for-reports/${schoolId}/review`);
    } else if (categoryName === 'Student Journey Workshop photo upload') {
      navigate(`${NAVIGATION_LINKS_DATA.SJW_PHOTO_UPLOAD.path}/${schoolId}`);
    } else if (categoryName === 'School report') {
      navigate('/school-reports');
    } else {
      const homePath =
        userDetails.role === USER_ROLES.FACILITATOR || userDetails.role === USER_ROLES.LEAD_FACILITATOR
          ? NAVIGATION_LINKS_DATA.DASHBOARD.path
          : NAVIGATION_LINKS_DATA.OVERVIEW.path;
      navigate(homePath);
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '';
    const date = dayjs(dateString);
    const time = date.format('h:mm a').toLowerCase();
    const dayOfWeek = date.format('dddd');
    const monthName = date.format('MMMM');
    const day = date.format('D');
    return `${time}, ${dayOfWeek}, ${monthName} ${day}`;
  };

  const fetchData = useCallback(
    async ({ showLoading = true } = {}) => {
      if (!userDetails) return;

      try {
        if (showLoading) {
          setLoading(true);
        }
        const [eoaConfigResponse, schoolsResponse, workflowStatusesResponse] = await Promise.all([
          getEOAConfig(userDetails.eoaId, userDetails.access_token),
          getConfiguredSchools(userDetails.eoaId, userDetails.access_token),
          getWorkflowStatuses(userDetails.eoaId, userDetails.access_token).catch(() => ({
            schools: {} as Record<string, SchoolWorkflowStatuses>,
          })),
        ]);

        setSjwDate(eoaConfigResponse.data.sjw_date);

        const configuredSchools: ConfiguredEOASchool[] = schoolsResponse.data;
        const primarySchools = configuredSchools.filter((school) => school.is_primary_on_eoa !== false);
        const workflowStatuses: Record<string, SchoolWorkflowStatuses> = workflowStatusesResponse.schools;

        const schoolsWithStatus = primarySchools.map((school: ConfiguredEOASchool) => {
          const schoolWorkflowStatuses = workflowStatuses[school.id] || {
            focus_group_report: null,
            transcript_selection: null,
            school_report: null,
            quote_selection: null,
            survey_report: null,
            sjw_photo_upload: null,
          };

          const categoryStatuses: CategoryStatus = {
            surveyReport: schoolWorkflowStatuses.survey_report,
            focusGroupReport: schoolWorkflowStatuses.focus_group_report,
            transcriptSelection: schoolWorkflowStatuses.transcript_selection,
            studentVoice: schoolWorkflowStatuses.quote_selection,
            schoolReport: schoolWorkflowStatuses.school_report,
            sjwPhotoUpload: schoolWorkflowStatuses.sjw_photo_upload,
          };

          const categoryDetails = getCategoryDetails(
            { id: school.id, name: school.name, categoryStatuses, totalCategories: 0 },
            eoaConfigResponse.data.sjw_date,
            userDetails.role
          );

          return {
            id: school.id,
            name: school.name,
            totalCategories: categoryDetails.length,
            categoryStatuses,
          };
        });

        setSchoolsProgress(schoolsWithStatus);
      } catch (error) {
        // Error handling - could be enhanced with error reporting
      } finally {
        setLoading(false);
      }
    },
    [userDetails]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useSchoolReportStatusChannel(() => {
    fetchData({ showLoading: false });
  });

  const workshops: Workshop[] = sjwDate
    ? [
        {
          title: 'Workshop 1',
          date: sjwDate,
          monthAbbr: dayjs(sjwDate).format('MMM').toUpperCase(),
          day: dayjs(sjwDate).format('D'),
          formattedDateTime: formatDateTime(sjwDate),
        },
      ]
    : [];

  const filteredWorkshops = workshops.filter((workshop) => {
    if (!workshop.date) return false;
    const workshopDate = dayjs(workshop.date).startOf('day');
    const today = dayjs().startOf('day');
    return selectedTab === 0 ? !workshopDate.isBefore(today) : workshopDate.isBefore(today);
  });

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      {(userDetails.role === USER_ROLES.LEAD_FACILITATOR || userDetails.role === USER_ROLES.XQ_ADMIN) && (
        <Panel className="transcript-selections-overview-panel">
          <h2 className="transcript-selections-panel-title">Student journey workshop</h2>

          <div className="dashboard-tabs-container">
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              aria-label="workshop tabs"
              className="dashboard-workshop-tabs"
              sx={sharedTabStyles.tabsContainer}
            >
              <Tab
                label="Upcoming"
                id="workshop-tab-0"
                aria-controls="workshop-tabpanel-0"
                className="dashboard-tab"
                sx={sharedTabStyles.tabButton}
              />
              <Tab
                label="Past workshop"
                id="workshop-tab-1"
                aria-controls="workshop-tabpanel-1"
                className="dashboard-tab"
                sx={sharedTabStyles.tabButton}
              />
            </Tabs>
          </div>

          <div className="dashboard-workshops-table">
            {filteredWorkshops.length > 0 ? (
              <TableContainer component={Paper} className="dashboard-table-container">
                <Table>
                  <TableBody>
                    {filteredWorkshops.map((workshop, index) => (
                      <TableRow key={index} className="dashboard-workshop-row">
                        <TableCell className="dashboard-workshop-date-cell">
                          <div className="dashboard-workshop-month">{workshop.monthAbbr}</div>
                          <div className="dashboard-workshop-day">{workshop.day}</div>
                        </TableCell>
                        <TableCell className="dashboard-workshop-content-cell">
                          <div className="dashboard-workshop-title">{workshop.title}</div>
                          <div className="dashboard-workshop-datetime">{workshop.formattedDateTime}</div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <div className="dashboard-workshop-empty">
                No {selectedTab === 0 ? 'upcoming' : 'past'} workshops found
              </div>
            )}
          </div>
        </Panel>
      )}

      <div className="dashboard-progress-status">
        <h2 className="dashboard-progress-status-title">Progress status</h2>
        <div className="dashboard-schools-list">
          {schoolsProgress.map((school) => {
            const isExpanded = expandedSchools.has(school.id);
            const categoryDetails = getCategoryDetails(school, sjwDate, userDetails.role);

            return (
              <div key={school.id} className={`dashboard-school-section ${isExpanded ? 'expanded' : ''}`}>
                <div
                  className={`dashboard-school-item ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleSchoolExpansion(school.id)}
                >
                  <div className="dashboard-school-content">
                    <span className="dashboard-school-name">{school.name}</span>
                    <SchoolProgressTag
                      categoryStatuses={school.categoryStatuses}
                      totalCategories={school.totalCategories}
                      userRole={userDetails.role}
                    />
                  </div>
                  {isExpanded ? (
                    <KeyboardArrowUpIcon className="dashboard-school-chevron" />
                  ) : (
                    <KeyboardArrowDownIcon className="dashboard-school-chevron" />
                  )}
                </div>
                {isExpanded && (
                  <div className="dashboard-school-categories">
                    <TableContainer component={Paper} className="dashboard-categories-table-container">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Category</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Due date</TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {categoryDetails.map((category, index) => (
                            <TableRow key={index} className="dashboard-category-row">
                              <TableCell className="dashboard-category-cell">{category.name}</TableCell>
                              <TableCell className="dashboard-category-cell">{category.statusComponent}</TableCell>
                              <TableCell className="dashboard-category-cell">{category.dueDate}</TableCell>
                              <TableCell className="dashboard-category-cell">
                                <ArrowForwardIosOutlinedIcon
                                  className="dashboard-category-chevron"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCategoryNavigation(category.name, school.id);
                                  }}
                                  aria-label={`Navigate to ${category.name}`}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default ProgressStatusView;
